/**
 * Pure helpers for composing the Rehab drawer's scope view from
 * `rehab_line_items` rows.
 *
 * Each row carries its own state inline — `estimated_cost` for budget,
 * `actual_cost` for the spent amount, `status` for done/pending, and
 * `category` to distinguish preset ("standard") scope items from
 * user-added ("custom") ones.
 *
 * This module is intentionally storage-shape-agnostic from the UI's
 * perspective: it converts DB rows into the flat `ScopeItem[]` that
 * the section + chart components consume.
 */
import type { RehabLineItemRow } from "@/types/pipeline";

/** Default labels surfaced when a project has no line items yet. */
export const REHAB_DEFAULT_LABELS = [
  "Interior paint & flooring",
  "Kitchen refresh",
  "Bath updates",
  "HVAC / electrical / plumbing",
  "Exterior & landscaping",
  "Contingency",
] as const;

/** Default split for the labels above (sums to 1.0; matches design). */
export const REHAB_DEFAULT_SPLITS = [0.28, 0.22, 0.15, 0.18, 0.1, 0.07] as const;

/**
 * Scope items surfaced in the UI: those with an estimated cost (budget > 0).
 * Zero-budget rows are empty placeholders — hidden from the task list, the
 * Budget vs Actual chart, and excluded from the completion progress %, so all
 * three stay in agreement.
 */
export function visibleScopeItems(items: ScopeItem[]): ScopeItem[] {
  return items.filter((i) => i.budget > 0);
}

/**
 * Money counted as spent against one scope item.
 *
 * Spend is recognized on completion: an item contributes its recorded actual
 * cost once its status is `completed`, and nothing while it's pending. So
 * "Budget Used" tracks money against work actually finished — an amount typed
 * against a task that isn't checked off yet never inflates the bar, while a
 * completed task that ran over its estimate still shows the overage.
 *
 * Checking a task auto-fills its actual from the estimate when nothing has
 * been recorded yet, so a completed row normally carries a real amount.
 */
export function recognizedSpend(item: ScopeItem): number {
  return item.done ? Number(item.actual) || 0 : 0;
}

/** Sum of `recognizedSpend` across a set of scope items. */
export function totalRecognizedSpend(items: ScopeItem[]): number {
  return items.reduce((sum, i) => sum + recognizedSpend(i), 0);
}

export interface ScopeItem {
  /** Stable React key — the DB row id when present, otherwise a synthetic placeholder. */
  key: string;
  /** DB row id. Null for synthesized fallback rows (when the project has no scope yet). */
  id: string | null;
  /** Display label. */
  label: string;
  /** Estimated cost for this scope item. */
  budget: number;
  /** Actual money spent against this scope item. */
  actual: number;
  /** True when the row's status is "completed". */
  done: boolean;
  /** ISO timestamp the item was marked completed; null while pending. */
  completedAt: string | null;
  /** True when the row's category is "custom" (deletable in the drawer). */
  isCustom: boolean;
}

/**
 * Compose scope items for the drawer. When the project has no rows yet,
 * falls back to the design's default labels with budgets derived from
 * `fallbackBudget` so the user always sees a starter scope. Fallback rows
 * have `id: null` and cannot be toggled / edited — the user must save on
 * the Find page first to materialize real rows.
 */
export function composeScopeItems(
  rows: RehabLineItemRow[] | null | undefined,
  fallbackBudget = 0,
): ScopeItem[] {
  if (rows && rows.length > 0) {
    return rows.map((row) => ({
      key: row.id,
      id: row.id,
      label: row.label ?? "",
      budget: Number(row.estimated_cost) || 0,
      actual: Number(row.actual_cost) || 0,
      done: row.status === "completed",
      completedAt: row.completed_at ?? null,
      isCustom: row.category === "custom",
    }));
  }
  return REHAB_DEFAULT_LABELS.map((label, i) => ({
    key: `fallback-${i}`,
    id: null,
    label,
    budget: Math.round(fallbackBudget * REHAB_DEFAULT_SPLITS[i]),
    actual: 0,
    done: false,
    completedAt: null,
    isCustom: false,
  }));
}
